// ./src/components/features/Connectors/ConfigEditors/AwsS3ConnectorConfigEditor.tsx
import React, { useState, useEffect, useCallback } from "react";
import {
  Stack,
  TextInput,
  Text,
  Anchor,
  Alert,
  Code,
  Select,
} from "@mantine/core";
import { IconInfoCircle } from "@tabler/icons-react";
import { stringifyObject, parseObject } from "@/utils";
import { AwsS3ConnectorConfig } from "@/types";

export interface AwsS3ConnectorConfigEditorProps {
  /** JSON string of connector configuration */
  value: string;

  /** Callback when configuration changes */
  onChange: (value: string) => void;

  /** Whether the editor is disabled */
  disabled?: boolean;
}

// Default empty configuration
const DEFAULT_CONFIG: AwsS3ConnectorConfig = {
  accessKeyId: "",
  secretAccessKey: "",
  region: "",
  bucketName: "",
  pathPrefix: "",
  fileNamePattern: "{{requestId}}.json",
};

// Common AWS regions
const AWS_REGIONS = [
  { value: "us-east-1", label: "US East (N. Virginia)" },
  { value: "us-east-2", label: "US East (Ohio)" },
  { value: "us-west-1", label: "US West (N. California)" },
  { value: "us-west-2", label: "US West (Oregon)" },
  { value: "ca-central-1", label: "Canada (Central)" },
  { value: "eu-west-1", label: "Europe (Ireland)" },
  { value: "eu-west-2", label: "Europe (London)" },
  { value: "eu-west-3", label: "Europe (Paris)" },
  { value: "eu-central-1", label: "Europe (Frankfurt)" },
  { value: "eu-north-1", label: "Europe (Stockholm)" },
  { value: "ap-northeast-1", label: "Asia Pacific (Tokyo)" },
  { value: "ap-northeast-2", label: "Asia Pacific (Seoul)" },
  { value: "ap-southeast-1", label: "Asia Pacific (Singapore)" },
  { value: "ap-southeast-2", label: "Asia Pacific (Sydney)" },
  { value: "ap-south-1", label: "Asia Pacific (Mumbai)" },
  { value: "sa-east-1", label: "South America (São Paulo)" },
];

/**
 * Parse and validate AWS S3 connector configuration
 */
const parseAwsS3Config = (jsonString: string): AwsS3ConnectorConfig => {
  const parsed = parseObject(jsonString, DEFAULT_CONFIG);
  return parsed;
};

export const AwsS3ConnectorConfigEditor: React.FC<
  AwsS3ConnectorConfigEditorProps
> = ({ value, onChange, disabled = false }) => {
  // Parse incoming value to config state
  const [config, setConfig] = useState<AwsS3ConnectorConfig>(() =>
    parseAwsS3Config(value)
  );

  // Sync config when external value changes
  useEffect(() => {
    const parsed = parseAwsS3Config(value);
    setConfig(parsed);
  }, [value]);

  // Update parent when config changes
  const updateConfig = useCallback(
    (newConfig: AwsS3ConnectorConfig) => {
      setConfig(newConfig);
      onChange(stringifyObject(newConfig, DEFAULT_CONFIG));
    },
    [onChange]
  );

  // Handle field changes
  const handleAccessKeyIdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    updateConfig({ ...config, accessKeyId: e.target.value });
  };

  const handleSecretAccessKeyChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    updateConfig({ ...config, secretAccessKey: e.target.value });
  };

  const handleRegionChange = (value: string | null) => {
    updateConfig({ ...config, region: value || "" });
  };

  const handleBucketNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    updateConfig({ ...config, bucketName: e.target.value.toLowerCase() });
  };

  const handlePathPrefixChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    updateConfig({ ...config, pathPrefix: e.target.value });
  };

  const handleFileNamePatternChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    updateConfig({ ...config, fileNamePattern: e.target.value });
  };

  return (
    <Stack gap="md">
      {/* Help Alert */}
      <Alert icon={<IconInfoCircle size={16} />} color="blue" variant="light">
        <Text size="sm">
          Create access keys in{" "}
          <Anchor
            href="https://console.aws.amazon.com/iam/"
            target="_blank"
            rel="noopener noreferrer"
          >
            AWS IAM Console
          </Anchor>
          {" → Users → Security credentials → Access keys"}
        </Text>
      </Alert>

      {/* Access Key ID */}
      <TextInput
        label="Access Key ID"
        placeholder="AKIAIOSFODNN7EXAMPLE"
        description="AWS Access Key ID"
        value={config.accessKeyId}
        onChange={handleAccessKeyIdChange}
        disabled={disabled}
        required
        size="md"
      />

      {/* Secret Access Key */}
      <TextInput
        label="Secret Access Key"
        placeholder="wJalrXUtnFEMI/K7MDENG/bPxRfiCYEXAMPLEKEY"
        description="AWS Secret Access Key (shown only once when created)"
        value={config.secretAccessKey}
        onChange={handleSecretAccessKeyChange}
        disabled={disabled}
        required
        size="md"
        type="password"
        autoComplete="off"
      />

      {/* Region */}
      <Select
        label="Region"
        placeholder="Select AWS region"
        description="AWS region where the bucket is located"
        data={AWS_REGIONS}
        value={config.region}
        onChange={handleRegionChange}
        disabled={disabled}
        required
        size="md"
        searchable
      />

      {/* Bucket Name */}
      <TextInput
        label="Bucket Name"
        placeholder="my-form-submissions"
        description="S3 bucket name (must already exist)"
        value={config.bucketName}
        onChange={handleBucketNameChange}
        disabled={disabled}
        required
        size="md"
      />

      {/* Path Prefix */}
      <TextInput
        label="Path Prefix"
        placeholder="submissions/{{formName}}/"
        description="Optional folder path for organizing files"
        value={config.pathPrefix || ""}
        onChange={handlePathPrefixChange}
        disabled={disabled}
        size="md"
      />

      {/* File Name Pattern */}
      <TextInput
        label="File Name Pattern"
        placeholder="{{requestId}}.json"
        description="Pattern for naming uploaded files"
        value={config.fileNamePattern || "{{requestId}}.json"}
        onChange={handleFileNamePatternChange}
        disabled={disabled}
        size="md"
      />

      {/* Placeholders Help */}
      <Alert icon={<IconInfoCircle size={16} />} color="gray" variant="light">
        <Text size="sm" fw={500} mb={4}>
          Available placeholders:
        </Text>
        <Text size="xs" component="div">
          <Code>{"{{requestId}}"}</Code> — Submission ID (e.g.,
          20250530143022-ABCD)
          <br />
          <Code>{"{{formId}}"}</Code> — Form UUID
          <br />
          <Code>{"{{formName}}"}</Code> — Form name (sanitized)
          <br />
          <Code>{"{{timestamp}}"}</Code> — Unix timestamp
          <br />
          <Code>{"{{date}}"}</Code> — Date (YYYY-MM-DD)
          <br />
          <Code>{"{{randUuid}}"}</Code> — Random UUID
        </Text>
      </Alert>
    </Stack>
  );
};
